import type { MouseEventHandler } from "react";
import { useCallback, useEffect, useState } from "react";

import { useFormatMessage } from "@skilitics/localization";

import { Tooltip } from "../dataDisplay/Tooltip";
import { ConfirmationDialog } from "../dialogs";
import { isUserInput } from "../forms/controls/helpers";
import type { TestAttribute } from "../forms/shared";
import { zIndexes } from "../utilities";
import { Button, IconButton, type ButtonProps } from "./Buttons";
import { destructiveActionFormatStrings } from "./destructiveActionFormatStrings";

type ConfirmDestructiveButtonProps = TestAttribute &
  ButtonProps & {
    onDestroy: () => void;
    label?: string;
    tooltip?: string;
    header: string;
    message: string;
    disabled?: boolean;
    enabledKeyboardShortcut?: boolean;
  };

export const ConfirmDestructiveButton = ({
  dataTest,
  disabled = false,
  header,
  label,
  tooltip,
  message,
  onDestroy,
  enabledKeyboardShortcut,
  ...buttonProps
}: ConfirmDestructiveButtonProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const formatMessage = useFormatMessage(mergedFormatStrings);

  const handleClick: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      setShowDialog(true);
    },
    [setShowDialog],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (enabledKeyboardShortcut && e.key === "Delete" && !isUserInput(e.target)) {
        e.stopPropagation();
        setShowDialog(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [enabledKeyboardShortcut]);

  return (
    <>
      {label ? (
        <Button
          variant="outlined"
          onClick={handleClick}
          data-test={dataTest ?? "confirm-destructive-button"}
          disabled={disabled}
          {...buttonProps}
        >
          {label}
        </Button>
      ) : (
        <Tooltip
          title={tooltip ?? formatMessage("deleteTooltip")}
          slotProps={{ popper: { sx: { zIndex: zIndexes.triggersOverlayPopover + 1 } } }}
        >
          <span>
            <IconButton
              icon="trash-alt"
              onClick={handleClick}
              data-test={dataTest ?? "confirm-destructive-button"}
              disabled={disabled}
            />
          </span>
        </Tooltip>
      )}

      {showDialog && (
        <ConfirmationDialog
          header={header}
          message={message}
          open={showDialog}
          confirmLabel={formatMessage("confirmLabel")}
          cancelLabel={formatMessage("cancelLabel")}
          onConfirm={(confirmed) => {
            if (confirmed) {
              onDestroy();
            }
            setShowDialog(false);
          }}
        />
      )}
    </>
  );
};

const formatStrings = {
  deleteTooltip: {
    en: "Delete",
  },
};
const mergedFormatStrings = { ...formatStrings, ...destructiveActionFormatStrings };
